import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    const handleSession = async (userId: string, email: string | undefined) => {
      // Limpar cache para garantir dados frescos
      queryClient.clear();

      // Buscar profile para decidir destino
      const { data: profile } = await supabase
        .from("profiles")
        .select("user_type, full_name")
        .eq("id", userId)
        .maybeSingle();

      // Sync com Brevo (mesmo fluxo do signup por email)
      // Roda só quando vem do signup OAuth — best effort, não bloqueia o redirect
      try {
        await supabase.functions.invoke("create-brevo-contact", {
          body: {
            email,
            firstName: profile?.full_name ?? "",
            lastName: "",
            userId,
            userType: profile?.user_type ?? "candidate",
          },
        });
      } catch (err) {
        console.error("Brevo sync failed (non-blocking):", err);
      }

      toast({
        title: "Bem-vindo(a)!",
        description: "Login realizado com sucesso.",
      });

      // Empresas usam email/senha; OAuth aqui é sempre candidato
      if (profile?.user_type === "company") {
        navigate("/company/dashboard", { replace: true });
      } else {
        navigate("/candidate/dashboard", { replace: true });
      }
    };

    // 1. Tenta pegar sessão existente (caso já tenha sido processada)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session?.user) {
        handleSession(session.user.id, session.user.email);
      }
    });

    // 2. Escuta evento SIGNED_IN (caso esteja processando o token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "SIGNED_IN" && session?.user) {
        handleSession(session.user.id, session.user.email);
      }
    });

    // 3. Timeout de segurança: se nada acontecer em 8s, volta para login
    const timeout = setTimeout(() => {
      if (cancelled) return;
      toast({
        title: "Não foi possível concluir o login",
        description: "Tente novamente.",
        variant: "destructive",
      });
      navigate("/login", { replace: true });
    }, 8000);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center space-y-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
        <p className="text-muted-foreground">Finalizando seu login...</p>
      </div>
    </div>
  );
}
